// @flow

import React from 'react';
import './CustomerProductSlotList.css';
import classNames from 'classnames';
import _ from 'lodash/fp';
import {getParentConfig} from '../../reducers/customerOrderView';

export type IconProps = {
  fixedWidth?: boolean,
  isComplete: boolean,
  isOptional: boolean,
  isOrdered: boolean,
  isPartial?: boolean,
  isDisabled?: boolean,
};

const Icon = ({
  fixedWidth = false,
  isComplete,
  isOptional,
  isOrdered,
  isPartial,
  isDisabled = false,
}: IconProps) => (
  <i className={classNames({
    'fa': true,
    'fa-fw': !!fixedWidth,
    'ordered': !isDisabled && !!isOrdered && !isPartial,
    'partial': !isDisabled && !!isOrdered && !!isPartial,
    'fa-check': !isDisabled && !!isComplete && !!isOptional,
    'fa-check-circle-o': !isDisabled && !!isComplete && !isOptional,
    'fa-plus': !isDisabled && !isComplete && !!isOptional,
    'fa-circle-o': !isDisabled && !isComplete && !isOptional,
  })} />
);

export type RoomLabelProps = {
  room: Object,
  active: boolean,
  expanded: boolean,
  onClick: Function,
};

const RoomLabel = ({room, active, expanded, onClick}: RoomLabelProps) => (
  <div
    onClick={onClick}
    title={room.isOptional === true ? 'Endast tillvalsprodukter, måste ej väljas' : ''}
    className={classNames({
      'CustomerProductSlotList-room-label': true,
      active,
    })}>
    <div className="CustomerProductSlotList-room-check">
      <Icon
        isComplete={room.isComplete}
        isOptional={room.isOptional}
        isOrdered={room.isComplete} />
    </div>
    <div className="CustomerProductSlotList-room-title">
      {room.name}
    </div>
    <div className="CustomerProductSlotList-expanded">
      <i className={classNames({
        'fa fa-fw': true,
        'fa-angle-down': expanded,
        'fa-angle-right': !expanded,
      })} />
    </div>
  </div>
);

export type SlotLabelProps = {
  slot: Object,
  active: boolean,
  isComplete: boolean,
  isOptional: boolean,
  isOrdered: boolean,
  isPartial?: boolean,
  isDisabled?: boolean,
  isHidden?: boolean,
  onClick: Function,
};

const SlotLabel = ({
  slot,
  active,
  isComplete,
  isOptional,
  isOrdered,
  isPartial,
  isDisabled = false,
  isHidden = false,
  onClick,
}: SlotLabelProps) => (
  <div
    onClick={onClick}
    title={!!isDisabled
      ? 'Kan först väljas när val av föregående produkt är gjord'
      : !!isPartial
        ? 'Det finns fler val att göra baserat på detta val'
        : !!isOptional
          ? 'Tillvalsprodukt, måste ej väljas'
          : ''}
    className={classNames({
      'CustomerProductSlotList-slot-label': true,
      active,
    })}>
    <div className="CustomerProductSlotList-slot-check">
      {!isHidden && (
        <Icon
          fixedWidth={true}
          isDisabled={isDisabled}
          isComplete={isComplete}
          isOptional={isOptional}
          isOrdered={isOrdered}
          isPartial={isPartial} />
      )}
    </div>
    <div className="CustomerProductSlotList-slot-title">
      {slot.name}
    </div>
  </div>
);

export type AdvancedSlotProps = {
  config: any[],
  slot: Object,
  onClick: Function,
  orderRowBySlotId: any,
  isTop?: boolean,
  activeSelection: Object,
};

const AdvancedSlot = ({
  config,
  slot,
  onClick,
  orderRowBySlotId,
  isTop = false,
  activeSelection,
}: AdvancedSlotProps) => (
  <div className={classNames({
    'advanced-slot': !isTop,
  })}>
    {_.flow(
      _.filter(c => !c.isHidden),
      _.map(({
        config: c,
        children,
        isComplete,
        isOptional,
        isOrdered,
        isPartial,
        isDisabled,
        isHidden,
      }) => (
        <div
          key={c.id}>
          <SlotLabel
            slot={isTop ? slot : c}
            active={!!activeSelection.config && activeSelection.config.uuid === c.uuid}
            isComplete={isComplete && !isPartial}
            isOptional={isOptional}
            isOrdered={isOrdered}
            isPartial={isPartial}
            isDisabled={isDisabled}
            isHidden={isHidden}
            onClick={() => onClick(c)} />
          {!!c.children.length && !!activeSelection.slot && activeSelection.slot.id === slot.id && (
            <AdvancedSlot
              slot={slot}
              config={children}
              onClick={onClick}
              orderRowBySlotId={orderRowBySlotId}
              activeSelection={activeSelection} />
          )}
        </div>
      )),
    )(config)}
  </div>
);

const mapConfig = (slot, orderRowBySlotId, isParentOrdered, isTop = false) => c => {
  const hasOrderRow = !!_.some(r =>
    r.productSlotId === slot.id
    && _.some({id: r.configId}, c.configs)
    , orderRowBySlotId);
  let isOrdered = hasOrderRow;
  let isComplete = isOrdered;
  let isOptional = true;
  let isDisabled = false;
  let isHidden = false;

  if (isTop) {
    isOptional = !slot.standardProductId;
  } else {
    const parentConfig = getParentConfig(orderRowBySlotId, c, slot);
    isOptional = !parentConfig || !_.some({productTypeId: c.id}, parentConfig.standardProducts);
    isComplete = !!isComplete
      || (
        isOptional && (
          !parentConfig
          || !_.some({parentId: parentConfig.id}, slot.config)
        )
      );
    isOrdered = isOrdered || (isComplete && isParentOrdered);
    isDisabled = !parentConfig || !_.some({parentId: parentConfig.id}, c.configs);
    isHidden = !isParentOrdered || (isComplete && !hasOrderRow);
  }

  const children = _.map(mapConfig(slot, orderRowBySlotId, isOrdered || (isComplete && isParentOrdered)), c.children);
  const isPartial = isComplete && !_.every(c => (c.isComplete || c.isOptional) && !c.isPartial, children);

  return {
    config: c,
    children,
    isComplete,
    isOptional,
    isOrdered,
    isPartial,
    isHidden,
    isDisabled,
  };
};

export type CustomerProductSlotListProps = {
  rooms: any[],
  expanedRooms: Object,
  orderRowBySlotId: Object,
  onRoomClick: Function,
  onSlotClick: Function,
  onAdvancedClick: Function,
  activeSelection: Object,
};

const CustomerProductSlotList = ({
  rooms,
  expanedRooms,
  orderRowBySlotId,
  onRoomClick,
  onSlotClick,
  onAdvancedClick,
  activeSelection,
}: CustomerProductSlotListProps) => (
  <div className="CustomerProductSlotList">
    {_.map(r => (
      <div
        key={r.id}
        className="CustomerProductSlotList-room">
        <RoomLabel
          room={r}
          active={!!activeSelection.room && activeSelection.room.id === r.id}
          expanded={!!expanedRooms[r.id]}
          onClick={() => {
            onRoomClick(r.id);
            const firstSlot = r.productSlots[0];
            if (!!firstSlot) {
              if (!!firstSlot.advanced) {
                onAdvancedClick(r, firstSlot, firstSlot.configTree[0]);
              } else {
                onSlotClick(r, firstSlot);
              }
            }
          }} />

        {!!expanedRooms[r.id] &&
          <div className="CustomerProductSlotList-product-slots">
            {_.map(s => {

              if (!!s.advanced) {
                return (
                  <AdvancedSlot
                    key={s.id}
                    config={_.map(mapConfig(s, orderRowBySlotId, false, true), s.configTree)}
                    slot={s}
                    activeSelection={activeSelection}
                    orderRowBySlotId={orderRowBySlotId}
                    onClick={t => onAdvancedClick(r, s, t)}
                    isTop={true} />
                );
              } else {
                const isComplete = !!orderRowBySlotId[`${s.id}__`];
                return (
                  <SlotLabel
                    key={s.id}
                    slot={s}
                    active={!!activeSelection.slot && activeSelection.slot.id === s.id}
                    isComplete={isComplete}
                    isOptional={!s.defaultProductId}
                    isOrdered={isComplete}
                    onClick={() => onSlotClick(r, s)} />
                );
              }
            }, r.productSlots)}
          </div>
        }
      </div>
    ), rooms)}
  </div>
);

export default CustomerProductSlotList;



// WEBPACK FOOTER //
// ./src/components/CustomerProductSlotList/CustomerProductSlotList.js